import {
  Autocomplete,
  AutocompleteProps,
  CircularProgress,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { ControllerProps, FieldValues } from "react-hook-form";
import { SelectionOption } from "../../../types";
import { useField, useOptions } from "../../../hooks";

/**
 * Props for the SplitButton component.
 * @template TOption - The type of the options.
 */
export type ControlledSelectProps<
  TOption extends SelectionOption,
  TForm extends FieldValues
> = Required<Pick<ControllerProps<TForm>, "control" | "name">> &
  Omit<
    AutocompleteProps<TOption, false, undefined, false>,
    "options" | "renderInput"
  > & {
    /**
     * Can be:
     * - An array of options.
     * - A function that returns an array of options.
     * - A function that returns a Promise that resolves to an array of options.
     */
    getOptions: TOption[] | (() => TOption[]) | (() => Promise<TOption[]>); // TODO: debería aceptar opciones con tipo value distintos de number
    searchable?: boolean;
  } & Pick<TextFieldProps, "required" | "label" | "variant" | "size">;

export function ControlledSelect<
  TOption extends SelectionOption,
  TForm extends FieldValues
>(props: ControlledSelectProps<TOption, TForm>) {
  const {
    control,
    name,
    getOptions,
    label,
    disabled,
    required,
    variant,
    size,
    searchable,
    ...rest
  } = props;

  const { field, fieldState, formState } = useField<TOption, TForm>({
    name,
    control,
  });

  const { options, isSuccess, isLoading } = useOptions<TOption>({
    getOptions,
  });

  return (
    <Autocomplete
      options={options ? options : []}
      loading={isLoading}
      value={field.value || ""}
      onChange={(event, newValue) => {
        field.onChange(newValue || undefined);
      }}
      
      // getOptionKey={(option) => option.value}

      renderOption={(props, option) => {
        return (
          <MenuItem {...props} key={option.value}>
            {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        );
      }}
      isOptionEqualToValue={(option: TOption, value: TOption) =>
        option.value === value.value
      }
      filterOptions={
        searchable
          ? (options, state) =>
              options.filter(
                (option) =>
                  option.label
                    .toLowerCase()
                    .includes(state.inputValue.toLowerCase())
                // TODO: se puede mejorar el filtro para que sea de tipo "localeContains"
              )
          : undefined
      }
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            label={props.label}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            required={props.required}
            variant={variant}
            size={size}
            inputProps={{
              ...params.inputProps,
              readOnly: !searchable,
              style: {
                cursor: !searchable ? "pointer" : undefined,
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              startAdornment: field.value?.icon ? (
                <InputAdornment position="start">
                  {field.value?.icon}
                </InputAdornment>
              ) : undefined,
              // value: field.value?.label,
              sx: {
                cursor: !searchable ? "pointer" : undefined,
              },
            }}
          />
        );
      }}
      {...rest}
    />
  );
}
